<script setup>
import { useRouter } from "vue-router";
import AppDataTable from "@/components/AppDataTable.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useResourceList } from "@/composables/useResourceList";
import { fetchBoardGames, deleteBoardGame } from "./api";

const router = useRouter();
const columns = [
  { key: "alt", label: "Game", sortable: true },
  { key: "gameName", label: "Game Name" },
  { key: "type", label: "Score Type" },
  { key: "startDate", label: "Start Date", sortable: true },
  { key: "endDate", label: "End Date", sortable: true },
  { key: "order", label: "Order", sortable: true },
  { key: "actions", label: "Actions", align: "end", width: "180px" },
];

const { rows, loading, error, pendingDelete, deleting, load, askDelete, confirmDelete } =
  useResourceList({
    fetch: fetchBoardGames,
    remove: (id) => deleteBoardGame(id),
    mapRow: (g) => ({
      id: g.gameName,
      gameName: g.gameName,
      alt: g.alt,
      type: g.type,
      startDate: g.startDate,
      endDate: g.endDate,
      order: g.order,
    }),
    sort: (a, b) => (a.order ?? 0) - (b.order ?? 0),
  });
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 gap-3 flex-wrap">
      <h1 class="text-h5">Board Games</h1>
      <div class="d-flex gap-2">
        <VBtn variant="tonal" @click="load">Refresh</VBtn>
        <VBtn @click="router.push({ name: 'board-games-new' })">Add Game</VBtn>
      </div>
    </div>
    <VAlert v-if="error" type="error" closable class="mb-4" @click:close="error = ''">{{ error }}</VAlert>
    <AppDataTable
      :columns="columns"
      :rows="rows"
      :loading="loading"
      row-key="id"
      searchable
      search-placeholder="Search games…"
      empty-text="No board games configured yet."
    >
      <template #cell-actions="{ row }">
        <VBtn size="small" variant="text" @click.stop="router.push({ name: 'board-games-edit', params: { id: row.id } })">Edit</VBtn>
        <VBtn size="small" color="error" variant="text" @click.stop="askDelete(row)">Delete</VBtn>
      </template>
    </AppDataTable>
    <ConfirmDialog
      :model-value="pendingDelete !== null"
      title="Delete game?"
      :message="`Delete &quot;${pendingDelete?.alt || 'this game'}&quot;? It will disappear from the Board Games page.`"
      :loading="deleting"
      @update:model-value="pendingDelete = null"
      @confirm="confirmDelete"
    />
  </section>
</template>
